import { useQuery } from "@apollo/client";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { StatDisplay } from "components/StatDisplay";

import { FACILITY_ACCUMULATION_SUMMARY_QUERY, TENANT_ACCUMULATION_SUMMARY_QUERY } from "./api";

/** Matches the KPI cards on the waste dashboards. */
const TILE_MIN_HEIGHT = 96;

type TileCounts = {
  activeContainerCount: number;
  approachingDeadlineContainerCount: number;
  overdueContainerCount: number;
};

/**
 * One count on the specs' grey tile: the label above, the number below behind
 * the emoji the specs draw.
 *
 * An emoji rather than an icon component, per the specs — it carries the whole
 * glyph including its color, which is why the number beside it still takes a
 * semantic color of its own for the two counts that mean something is due.
 * `aria-hidden` because the label already says what the count is; a screen
 * reader announcing "bucket 48" would be worse than "Active containers, 48".
 */
function ContainerStatTile({
  label,
  value,
  emoji,
  color,
}: {
  label: string;
  value: number;
  emoji: string;
  /** Semantic color for the number; omitted for a neutral count. */
  color?: "warning" | "error";
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: TILE_MIN_HEIGHT,
        px: 2,
        py: 1,
        backgroundColor: (theme) => theme.palette.grey50.main,
        borderRadius: 3,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <StatDisplay
        label={label}
        value={
          <Stack direction="row" spacing={1} alignItems="center" component="span">
            <Box component="span" aria-hidden sx={{ fontSize: "1.75rem", lineHeight: 1 }}>
              {emoji}
            </Box>
            <Typography variant="h4" component="span" color={color ? `${color}.main` : undefined}>
              {value}
            </Typography>
          </Stack>
        }
      />
    </Box>
  );
}

function ContainerStatTileRow({ counts }: { counts: TileCounts }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
      <ContainerStatTile label="Active containers" value={counts.activeContainerCount} emoji="🪣" />
      {/* Not "must ship within 30 days", which the specs draw: the count also
          includes satellite containers inside their one-day move window, and
          those have to move to central accumulation rather than ship. The
          facility dashboard names the same field the same way. */}
      <ContainerStatTile
        label="Approaching deadline"
        value={counts.approachingDeadlineContainerCount}
        emoji="⏳"
        color="warning"
      />
      <ContainerStatTile
        label="Overdue"
        value={counts.overdueContainerCount}
        emoji="⏰"
        color="error"
      />
    </Stack>
  );
}

function TileRowSkeleton() {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} variant="rounded" height={TILE_MIN_HEIGHT} sx={{ flex: 1 }} />
      ))}
    </Stack>
  );
}

/**
 * The headline counts above the containers list, for whichever scope the list
 * is showing.
 *
 * Two queries rather than one parameterized field: the counts are a rollup of
 * different populations, and the facility form already existed for the
 * facility dashboard. Which one runs follows the list's own scope, so the
 * tiles always describe exactly the rows underneath them.
 */
export function ContainerStatTiles({
  tenantId,
  facilityId,
}: {
  tenantId: string;
  /** Present when the list is scoped to one facility. */
  facilityId?: string;
}) {
  const { data: tenantData, loading: tenantLoading } = useQuery(TENANT_ACCUMULATION_SUMMARY_QUERY, {
    variables: { tenantId },
    skip: !!facilityId,
    // Containers are created and shipped from other routes; revalidate on
    // mount so the counts aren't a cached snapshot from the last visit.
    fetchPolicy: "cache-and-network",
  });
  // The facility dashboard's own summary query, reused rather than a narrower
  // copy: every container mutation already lists it for refetch, so the tiles
  // stay correct after a create or a ship without a second name to remember.
  const { data: facilityData, loading: facilityLoading } = useQuery(
    FACILITY_ACCUMULATION_SUMMARY_QUERY,
    {
      variables: { facilityId: facilityId ?? "" },
      skip: !facilityId,
      fetchPolicy: "cache-and-network",
    }
  );

  const counts = facilityId
    ? facilityData?.facility?.accumulationSummary
    : tenantData?.tenant?.accumulationSummary;
  const loading = facilityId ? facilityLoading : tenantLoading;

  if (!counts) {
    return loading ? <TileRowSkeleton /> : null;
  }
  return <ContainerStatTileRow counts={counts} />;
}
